/**
 * Add or Edit Trade Account Manager
 * Main component that manages the 4-step process for adding/editing trade accounts
 *
 * Steps:
 * 1. API Details Entry - Save broker connection details to database
 * 2. Broker Login - Handle broker platform login via URL and status monitoring
 * 3. Validation - Validate session and connection
 * 4. Complete - Show success and completion status
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { services } from "../../../services/api";
import { PageHeader } from "../../../components/ds";
import ProgressTracker from "../Components/ProgressTracker";
import LoadingSpinner from "../Components/LoadingSpinner";
import AlertMessage from "../Components/AlertMessage";
import APIDetailsStep from "./Steps/APIDetailsStepForm";
import APIDetailsStepPage from "./Steps/APIDetailsStepMainPage";
import BrokerLoginStepPage from "./Steps/BrokerLoginStepPage";
import ValidationStep from "./Steps/ValidationStep";
import CompletionStep from "./Steps/CompletionStep";
import BrokerInstructions from "./Components/BrokerInstructions";

const AddEditTradeAccount = () => {
  const { accountId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine if we're in edit mode
  const isEditMode = Boolean(accountId);

  // Component state
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [availableBrokers, setAvailableBrokers] = useState([]);
  const [appName, setAppName] = useState("DeeTrade");

  // Memoized callback to prevent unnecessary re-renders
  const handleAppNameChange = useCallback((newAppName) => {
    setAppName(newAppName);
  }, []);

  // Step progress tracking
  const [steps, setSteps] = useState([
    {
      title: "API Details",
      description: "Enter your broker API credentials",
      status: "pending",
    },
    {
      title: "Broker Login",
      description: "Login to your broker platform",
      status: "pending",
    },
    {
      title: "Validation",
      description: "Validate connection",
      status: "pending",
    },
    {
      title: "Complete",
      description: "Setup completed successfully",
      status: "pending",
    },
  ]);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, [accountId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load available brokers
      const brokersResponse =
        await services.brokerPlatforms.getAvailableBrokers();
      console.log("Available Brokers Response:", brokersResponse);

      // Fix API response parsing - brokers are in brokersResponse.data.data
      // The API returns { data: { data: [...brokers], status: "success" } }
      const rawBrokers =
        brokersResponse.data && brokersResponse.data.data
          ? brokersResponse.data.data
          : [];
      console.log("Raw Brokers:", rawBrokers);

      // Map broker properties to expected format for form components
      const mappedBrokers = rawBrokers.map((broker) => ({
        id: broker.broker_id,
        name: broker.display_name || broker.name,
        code: broker.code,
        country: broker.country,
        country_code: broker.country_code,
        logo: broker.logo,
        instructions: broker.instructions,
        requires_totp: broker.requires_totp,
        // Include original broker data for compatibility
        ...broker,
      }));

      console.log("Mapped Brokers:", mappedBrokers);
      setAvailableBrokers(mappedBrokers);
      console.log("Edit mode for accountId:", accountId);
      // If editing, load existing account data
      if (isEditMode && accountId) {
        const accountResponse = await services.brokerAccounts.getAccount(
          accountId
        );
        // Check and handle nested data structure for account response too
        const accountData =
          accountResponse.data && accountResponse.data.data
            ? accountResponse.data.data
            : accountResponse.data;

        // For edit mode, also fetch decrypted credentials to populate form fields
        console.log(
          "Edit mode: fetching decrypted credentials for account:",
          accountId
        );
        try {
          const credentialsResponse =
            await services.brokerAccounts.getDecryptedCredentials(accountId);
          console.log("Decrypted credentials response:", credentialsResponse);

          // Merge the account data with decrypted credentials
          const credentialsData =
            credentialsResponse.data && credentialsResponse.data.data
              ? credentialsResponse.data.data
              : credentialsResponse.data;

          // Combine account data with decrypted credentials for form population
          const enhancedAccountData = {
            ...accountData,
            // Override with decrypted credentials for form fields
            api_key: credentialsData.api_key || accountData.api_key,
            api_secret:
              credentialsData.api_secret_key ||
              credentialsData.api_secret ||
              accountData.api_secret,
            api_secret_key:
              credentialsData.api_secret_key ||
              credentialsData.api_secret ||
              accountData.api_secret_key,
          };

          console.log(
            "Enhanced account data with decrypted credentials:",
            enhancedAccountData
          );
          setAccountData(enhancedAccountData);
        } catch (credentialsError) {
          console.error(
            "Failed to fetch decrypted credentials:",
            credentialsError
          );
          // If credentials fetch fails, use the regular account data
          setAccountData(accountData);
        }

        // Find the broker for this account
        if (accountData && accountData.broker_id) {
          const broker = mappedBrokers?.find(
            (b) => b.id === accountData.broker_id
          );
          setSelectedBroker(broker);
          // Set app name from existing account data
          if (accountData.app_name) {
            setAppName(accountData.app_name);
          }
        }
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
      setError(
        `Failed to load initial data: ${err.message || "Unknown error"}`
      );
      // Set empty brokers array to prevent further errors
      setAvailableBrokers([]);
    } finally {
      setLoading(false);
    }
  };

  // Update step status
  const updateStepStatus = (stepIndex, status) => {
    setSteps((prev) =>
      prev.map((step, index) =>
        index === stepIndex ? { ...step, status } : step
      )
    );
  };

  // Move to next step
  const nextStep = () => {
    if (currentStep < 4) {
      updateStepStatus(currentStep - 1, "completed");
      setCurrentStep(currentStep + 1);
    }
  };

  // Go back to previous step
  const previousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      updateStepStatus(currentStep - 1, "pending");
    }
  };

  // Handle API details submission (Step 1)
  const handleApiDetailsSubmit = async (formData) => {
    try {
      setLoading(true);
      updateStepStatus(0, "pending");

      let response;
      if (isEditMode) {
        response = await services.brokerAccounts.updateAccount(
          accountId,
          formData
        );
      } else {
        response = await services.brokerAccounts.createAccount(formData);
      }
      console.log("API Details Submission Response:", response);
      if (response.data.status === "success") {
        setAccountData(response.data.data);
        updateStepStatus(0, "completed");
        nextStep();
      } else {
        throw new Error(
          response.error?.message || "Failed to save account details"
        );
      }
    } catch (err) {
      updateStepStatus(0, "error");
      setError(`Failed to save account: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle broker login completion (Step 2)
  const handleLoginSuccess = async () => {
    try {
      updateStepStatus(1, "completed");
      nextStep();
    } catch (err) {
      updateStepStatus(1, "error");
      setError(`Login process failed: ${err.message}`);
    }
  };

  // Handle validation completion (Step 3)
  const handleValidationSuccess = async () => {
    try {
      updateStepStatus(2, "completed");
      nextStep();
    } catch (err) {
      updateStepStatus(2, "error");
      setError(`Validation failed: ${err.message}`);
    }
  };

  // Handle completion (Step 4)
  const handleCompletion = () => {
    updateStepStatus(3, "completed");
    // Navigate back to view accounts after a delay
    setTimeout(() => {
      navigate("/TradeAccounts");
    }, 2000);
  };

  // Handle cancellation
  const handleCancel = () => {
    navigate("/TradeAccounts");
  };

  // Render current step - memoized to prevent unnecessary re-renders
  const renderCurrentStep = useCallback(() => {
    // Show warning if brokers couldn't be loaded
    if (
      currentStep === 1 &&
      !isEditMode &&
      availableBrokers.length === 0 &&
      !loading
    ) {
      return (
        <div className="text-center py-10">
          <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 mb-6">
            <h3 className="ds-h3 text-yellow-800 dark:text-yellow-300 mb-2">
              No Brokers Available
            </h3>
            <p className="text-yellow-700 dark:text-yellow-400 mb-4">
              We couldn't load the list of available brokers. This could be due
              to a network issue or the broker service may be temporarily
              unavailable.
            </p>
            <button
              onClick={loadInitialData}
              className="btn btn-secondary"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <APIDetailsStep
            isEditMode={isEditMode}
            accountData={accountData}
            availableBrokers={availableBrokers}
            selectedBroker={selectedBroker}
            onBrokerSelect={setSelectedBroker}
            onSubmit={handleApiDetailsSubmit}
            onCancel={handleCancel}
            loading={loading}
            appName={appName}
            onAppNameChange={handleAppNameChange}
          />
        );

      case 2:
        return (
          <BrokerLoginStepPage
            accountData={accountData}
            selectedBroker={selectedBroker}
            isEditMode={isEditMode}
            onLoginSuccess={handleLoginSuccess}
            onBack={previousStep}
            loading={loading}
          />
        );

      case 3:
        return (
          <ValidationStep
            accountData={accountData}
            selectedBroker={selectedBroker}
            onValidationSuccess={handleValidationSuccess}
            onBack={previousStep}
            loading={loading}
          />
        );

      case 4:
        return (
          <CompletionStep
            accountData={accountData}
            selectedBroker={selectedBroker}
            isEditMode={isEditMode}
            onComplete={handleCompletion}
          />
        );

      default:
        return null;
    }
  }, [
    currentStep,
    isEditMode,
    availableBrokers,
    loading,
    accountData,
    selectedBroker,
    handleApiDetailsSubmit,
    handleCancel,
    appName,
    handleAppNameChange,
    handleLoginSuccess,
    previousStep,
    handleValidationSuccess,
    handleCompletion,
  ]);

  if (loading && !accountData) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <LoadingSpinner message="Loading account details..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main-bg dark:bg-main-dark-bg pt-0 px-4 md:px-6 pb-10">
      <div>
        {/* Standardized page header (common across all pages) */}
        <PageHeader
          title={isEditMode ? "Edit Account" : "Add Account"}
          subtitle={isEditMode
            ? "Update your broker account connection details and verify the connection."
            : "Follow these steps to connect your broker trading account to DeeTrade."}
        />

        {/* Error Message */}
        {error && (
          <div className="mb-6">
            <AlertMessage
              severity="error"
              title="Error"
              message={error}
              onClose={() => setError(null)}
            />
          </div>
        )}

        {/* Progress Tracker */}
        <div className="bg-bg-2 rounded-lg shadow-sm border border-[var(--divider)] p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 lg:mb-12 dark:shadow-gray-900/30">
          <ProgressTracker steps={steps} currentStep={currentStep} />
        </div>

        {/* Main Content - Enhanced Responsive Layout */}
        {/* Step 1: Use dedicated page component */}
        {currentStep === 1 && selectedBroker ? (
          <APIDetailsStepPage
            selectedBroker={selectedBroker}
            isEditMode={isEditMode}
            accountData={accountData}
            availableBrokers={availableBrokers}
            onBrokerSelect={setSelectedBroker}
            onSubmit={handleApiDetailsSubmit}
            onCancel={handleCancel}
            loading={loading}
            appName={appName}
            onAppNameChange={handleAppNameChange}
          />
        ) : currentStep === 1 && !selectedBroker ? (
          /* Step 1: When no broker selected, show in single column */
          <div className="w-full max-w-5xl mx-auto">
            <div className="bg-bg-2 rounded-lg shadow-sm dark:shadow-gray-900/30 border border-[var(--divider)] p-4 sm:p-6 lg:p-8 xl:p-10 transition-all hover:shadow-md dark:hover:shadow-gray-900/40">
              {renderCurrentStep()}
            </div>
          </div>
        ) : currentStep === 2 && selectedBroker ? (
          /* Step 2: Full width layout for BrokerLoginStepPage */
          <div className="w-full">{renderCurrentStep()}</div>
        ) : (
          /* Single Column Layout for Steps 3 & 4 or when no broker selected */
          <div className="max-w-5xl mx-auto">
            <div className="bg-bg-2 rounded-lg shadow-sm dark:shadow-gray-900/30 border border-[var(--divider)] p-4 sm:p-6 lg:p-8 xl:p-10 transition-all hover:shadow-md dark:hover:shadow-gray-900/40">
              {renderCurrentStep()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddEditTradeAccount;
